<template>
    <div :class="`paypal-pay ${context === 'checkout' ? 'd-none' : ''}`" id="paypal-pay">
        <div class="paypal-button-container-cart1"></div>
    </div>
</template>

<script setup>
    import { ref, onActivated, onMounted, getCurrentInstance } from "vue";
    import { mapActions, mapState } from "@/store/helper/vuex";
    import { scrollAnimate } from "@/components/helper/util"
    import { useI18n } from 'vue-i18n';
    const widgetHelper = require("@/scripts/widgetHelper");
    const { onSubmitPaypal, cancelPaypal, onShopperDetails, updatePayPalOrder, setCurrentStage, updateAbandonedCart} = mapActions();

    const { actionURLs } = mapState();
    const { adyenPreferences } = window.vue.variables;
    const { t } = useI18n();
    const app = getCurrentInstance();
    const loadScript = app.appContext.config.globalProperties.$loadScript;

    const props = defineProps({
        context: String
    });

    let userAction = ref(false);

    const isGDPRPassed = () => {
        let gdprPolicy = document.getElementById('gdpr-policy');

        if (gdprPolicy && !gdprPolicy.checked) {
            gdprPolicy.classList.add('is-invalid');
            scrollAnimate(document.querySelector('#gdpr-policy'));
            return false;
        }

        return true;
    }

    const submitForm = (url, data) => {
        let form = document.createElement('form');

        let additionalDetails = document.createElement('input');
        additionalDetails.value = data;
        additionalDetails.name = 'additionalDetails';

        let location = document.createElement('input');
        location.value = props.context;
        location.name = 'location';

        form.method = 'POST';
        form.action = url;

        form.appendChild(additionalDetails);
        form.appendChild(location);

        document.body.appendChild(form);

        form.submit();
    }

    const paypalConfiguration = {
        intent: adyenPreferences.paypalIntent,
        isExpress: true,
        style: {
            layout: 'horizontal',
            color: 'white',
            size: 'responsive',
            height: 48
        },
        blockPayPalCreditButton: true,
        blockPayPalPayLaterButton: true,
        blockPayPalVenmoButton: true,
        onShippingAddressChange: async (data, actions, component) => {
            let payload =  JSON.stringify({
                shippingAddress: data.shippingAddress,
                paymentData: component.paymentData,
                location: props.context,
                userAction: userAction.value
            });
            return updatePayPalOrder(payload)
                .then((response) => {
                    userAction.value = true;
                    if (response && response.paymentData) {
                        component.updatePaymentData(response.paymentData);
                    } else {
                        return actions.reject(data.errors.ADDRESS_ERROR);
                    }
                });
        },
        onShippingOptionsChange: async (data, actions, component) => {
            let payload =  JSON.stringify({
                shippingAddress: data.shippingAddress,
                paymentData: component.paymentData,
                location: props.context,
                selectedShipping: data.selectedShippingOption.id
            });
            return updatePayPalOrder(payload)
                .then((response) => {
                    if (response && response.paymentData) {
                        component.updatePaymentData(response.paymentData);
                    } else {
                        return actions.reject(data.errors.METHOD_UNAVAILABLE);
                    }
                });
        },
        onSubmit: async (state, component) => {
            state.data.location = props.context;
            widgetHelper.spinner.start();
            onSubmitPaypal(JSON.stringify(state.data))
                .then((response) => {
                    if (response && response.action) {
                        component.handleAction(response.action);
                    } else {
                        if (response.goToStage) {
                            setCurrentStage(response.goToStage);
                        }
                        component.reject();
                    }
                });
            updateAbandonedCart('CHECKOUT_PAYMENT');
        },
        onError: async (error, component) => {
            cancelPaypal()
                .then(() => {
                    if (component) {
                        component.setStatus('ready');
                    }
                    widgetHelper.spinner.stop();
                });
        },
        onCancel: (data, component) => {
            if (component) {
                component.setStatus('ready');
            }
            widgetHelper.spinner.stop();
        },
        onShopperDetails: async (shopperDetails, rawData, actions) => {
            return onShopperDetails(JSON.stringify({ ...shopperDetails, location: props.context }))
                .then((response) => {
                    if (!response || response.error) {
                        widgetHelper.createError(response.errorMessage || t('error.process.request'));
                        const action = actions.reject();

                        if (response.redirectUrl) {
                            window.location = response.redirectUrl;
                        }

                        return action;
                    } else {
                        return actions.resolve();
                    }
                });
        },
        onAdditionalDetails: (state) => {
            window.EventEmitter?.emit('addPaymentInfo', { paymentMethod: 'PAYPAL' });
            submitForm(actionURLs.value['AdyenPaypal-OnAdditionalDetails'], JSON.stringify(state));
        },
        onClick: (data, actions) => {
            if (!isGDPRPassed()) {
                return actions.reject();
            }
            if (props.context !== 'checkout') {
                window.EventEmitter?.emit('beginExpressPayment', {
                    checkoutType: 'Express:PayPal'
                });
            }
            const paymentMethod = 'PAYPAL';
            document.dispatchEvent(new CustomEvent('gtm:addPaymentInfo', { detail: paymentMethod }));
            userAction.value = false;
            return actions.resolve();
        }
    }

    const addPaypalButton = async () => {
        if (typeof AdyenCheckout !== 'function') {
            return;
        }
        const configuration = {
            amount: {
                currency: adyenPreferences.currency
            },
            locale: adyenPreferences.locale,
            clientKey: adyenPreferences.clientKey,
            environment: adyenPreferences.environment
        };

        const paypalContainers = Array.from(document.getElementsByClassName('paypal-button-container-cart1'));
        if (paypalContainers.length) {
            const checkout = await new AdyenCheckout(configuration);

            paypalContainers.forEach((container) => {
                const parentPaypalDiv = container.parentElement || document.getElementById('paypal-pay');
                container.remove();

                const paypalContainer = document.createElement('div');
                paypalContainer.className = 'paypal-button-container-cart1';
                parentPaypalDiv?.appendChild(paypalContainer);

                checkout.create('paypal', paypalConfiguration).mount(paypalContainer);
            });
        }
    }

    onActivated(() => {
        if (typeof AdyenCheckout === 'function') {
            addPaypalButton();
        } else {
            loadScript(adyenPreferences.adyenCheckoutUrl, {}, {})
                .then(() => {
                    addPaypalButton();
                });
        }
    });

    onMounted(() => {
        if (typeof AdyenCheckout === 'function') {
            addPaypalButton();
        } else {
            loadScript(adyenPreferences.adyenCheckoutUrl, {}, {})
                .then(() => {
                    addPaypalButton();
                });
        }
    });
</script>